import { assignGroups, suggestGroupCount } from './groupingUtils';
import { getGroupLabel } from './groupLabels';
import { newId } from './idGen';

/**
 * Cross-Group Duals — the Ryder Cup / Davis Cup pairing structure: teams (or
 * any participants) are split into groups, and every entry faces every entry
 * in the OTHER group(s), never its own. Pure structure — WHO plays WHOM —
 * same as every other generator; it says nothing about how any one tie
 * resolves into games. That's a separate, orthogonal concern (see
 * tieFormats.js's MLP slate, which can layer on top of a cross_group tie, a
 * round_robin tie, or any other format's match, whenever both sides are real
 * 4-player team rosters).
 *
 * `options.legs` (default 1) repeats every tie that many times — e.g. legs: 3
 * for a best-of-3 series between the same two sides (India vs Australia,
 * 3 Tests) — same `legs` concept Round Robin already supports, just applied
 * within a tie instead of across a whole cycle. Legs after the first swap
 * slotA/slotB (the "return leg" reads as the away side's turn), matching
 * Round Robin's convention. All of one tie's legs stay grouped under that
 * tie's single round/heading — MatchListView already renders a round's
 * `matches` as however many rows it has, so a 3-leg tie just shows 3 rows
 * under one "Team A vs Team B" heading with no view-layer changes needed.
 */
export const generateCrossGroup = (participants, options = {}) => {
  const n = participants.length;
  // Floor is 2, not 4 — a pure bilateral series (India vs Australia: 1 team
  // per side) is the smallest valid case, same "groups face each other"
  // structure as a larger multi-team edition, just with 1 entry per group.
  if (n < 2) {
    throw new Error('Dual Meet / Series needs at least 2 participants (1 per side)');
  }

  const groupCount = Math.max(2, Math.min(options.groupCount || suggestGroupCount(n), Math.floor(n / 2)));
  // An explicit, possibly hand-edited assignment from the group assignment
  // editor takes priority over the automatic serpentine split.
  const groupedEntries = options.groupAssignments || assignGroups(participants, groupCount);
  const groups = groupedEntries.map((entries, idx) => ({ label: getGroupLabel('cross_group', idx, options.groupNames), entries }));

  if (groups.filter((g) => g.entries.length > 0).length < 2) {
    throw new Error('At least 2 groups need 1+ entry each for cross-group play');
  }

  // Every entry in one group faces every entry in every OTHER group, once —
  // never a same-group pairing. Shuffled so the match order isn't just "all
  // of group A's ties, then all of group B's."
  const pairs = [];
  for (let gi = 0; gi < groups.length; gi++) {
    for (let gj = gi + 1; gj < groups.length; gj++) {
      for (const entryA of groups[gi].entries) {
        for (const entryB of groups[gj].entries) {
          pairs.push({ entryA, entryB, groupLabelA: groups[gi].label, groupLabelB: groups[gj].label });
        }
      }
    }
  }

  if (pairs.length === 0) {
    throw new Error('No eligible ties — need at least 2 non-empty groups for cross-group play');
  }

  const shuffled = [...pairs];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  // A round HERE is one tie, played sequentially — this generator has no
  // real simultaneous-timeslot concept (see module doc comment), so a plain
  // shuffle can easily (and did, in practice — reported directly: a team
  // scheduled in round 1 AND round 2 back-to-back with only 4 teams/4 ties)
  // land the same team in two adjacent ties with room to space it out
  // further away. Same greedy "don't repeat the last one's identity unless
  // truly forced" reordering as scheduleRows.js's spaceOutAdjacentPlayers —
  // best-effort: with exactly 2 groups of 2 (4 ties total), the 4 ties'
  // conflict structure makes ONE adjacent repeat mathematically unavoidable
  // no matter the order, but every OTHER team gets spaced out correctly
  // instead of leaving that outcome to chance.
  const spacedOut = [];
  const remainingPairs = shuffled.map((pair) => ({ pair, teams: new Set([pair.entryA.id, pair.entryB.id]) }));
  let lastTeams = new Set();
  while (remainingPairs.length) {
    let index = remainingPairs.findIndex((entry) => ![...entry.teams].some((id) => lastTeams.has(id)));
    if (index === -1) index = 0;
    const [picked] = remainingPairs.splice(index, 1);
    spacedOut.push(picked.pair);
    lastTeams = picked.teams;
  }

  const legs = Math.max(1, options.legs || 1);
  // With only 2 groups (Dual Meet/Series' fixed case, or a plain Cross-Group
  // organizer-picked down to 2), "Group A vs Group B" is the SAME string for
  // every single tie — there's only ever one possible pair of groups, so it
  // adds nothing beyond the team names already shown right next to it. Only
  // worth showing once there are 3+ groups, where which two groups a given
  // tie actually connects varies match to match and isn't otherwise visible.
  const isTrivialGrouping = groups.length <= 2;

  // One round per tie — same simplification production's mlpScheduler.js
  // uses (no real parallel time-slot scheduling modeled here or anywhere
  // else in this rebuild yet), just generalized to the generic
  // {round, label, matches} shape every other generator already produces.
  // Sequential across the WHOLE schedule (every leg of every tie), not reset
  // per tie — consistent with how every other generator numbers matches.
  let matchNumber = 1;
  const rounds = spacedOut.map((pair, idx) => {
    const matches = Array.from({ length: legs }, (_, l) => {
      const leg = l + 1;
      const [slotA, slotB] = leg % 2 === 1 ? [pair.entryA, pair.entryB] : [pair.entryB, pair.entryA];
      const groupLabel = isTrivialGrouping
        ? (legs > 1 ? `Leg ${leg}` : undefined)
        : (legs > 1 ? `${pair.groupLabelA} vs ${pair.groupLabelB} · Leg ${leg}` : `${pair.groupLabelA} vs ${pair.groupLabelB}`);
      return {
        id: newId(),
        number: matchNumber++,
        round: idx + 1,
        slotA,
        slotB,
        groupLabel,
      };
    });
    return { id: newId(), round: idx + 1, label: `${pair.entryA.name} vs ${pair.entryB.name}`, matches };
  });

  return {
    formatId: 'cross_group',
    participantsCount: n,
    groupCount,
    groups,
    rounds,
    totalMatches: rounds.reduce((sum, r) => sum + r.matches.length, 0),
  };
};
